import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Checkbox,
  Divider,
  HStack,
  Heading,
  Text,
  Tooltip,
  VStack,
  Icon,
  useColorModeValue
} from '@chakra-ui/react';
import { Card, CardFooter, CardHeader, CardBody } from '@chakra-ui/card';
import { isMobile } from 'react-device-detect';
import { FaReceipt, FaCalendarAlt, FaCreditCard } from 'react-icons/fa';
import {
  MemberProductStatus
} from "../models/periodic-product-payment";
import CurrencyData from "./currency-data";
import {badgeColor, getBadgeLabel, isBlockedByPreviousUnpaidMonths, paymentCheckboxIsDisabled, PREVIOUS_MONTHS_PAYMENT_REQUIRED_MESSAGE} from "../helpers";

interface Props {
  membershipFee: MemberProductStatus;
  toPayList: MemberProductStatus[];
  allMembershipFees: MemberProductStatus[];
  onChange: any;
}

function MembershipFeeCard(props: Props): JSX.Element {
  const date =
    props.membershipFee.payments != null && props.membershipFee.payments.length > 0
      ? new Date(props.membershipFee.payments.at(props.membershipFee.payments.length-1)?.paymentDate ?? Date.now())
          .toLocaleDateString('es-AR')
          .split('T')[0]
      : new Date().toISOString();
  const [isChecked, setIsChecked] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const perforatedBorder = useColorModeValue('gray.300', 'gray.600');
  const headerBg = useColorModeValue('blue.50', 'blue.900');
  const headerBorderColor = useColorModeValue('blue.200', 'blue.700');
  const footerBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.300');
  const priceColor = useColorModeValue('green.600', 'green.300');
  const checkboxBg = useColorModeValue('orange.50', 'orange.900');
  const checkboxBorderColor = useColorModeValue('orange.200', 'orange.600');
  const checkboxTextColor = useColorModeValue('orange.700', 'orange.200');
  const perforation1 = useColorModeValue('transparent', 'transparent');
  const perforation2 = useColorModeValue('white', 'gray.800');
  const hoverBorderColor = useColorModeValue('blue.300', 'blue.600');
  const priceBg = useColorModeValue('green.50', 'green.900');
  const priceBorderColor = useColorModeValue('green.200', 'green.600');

  // Pre-calculate gradient to avoid kebab-case issues
  const perforationGradient = `repeating-linear-gradient(90deg, ${perforation1} 0px, ${perforation1} 8px, ${perforation2} 8px, ${perforation2} 16px)`;

  useEffect(() => {
    if (
      props.toPayList.find(
        (membershipFee) =>
          membershipFee.productId ===
          props.membershipFee.product.id
      ) != null
    ) {
      setIsChecked(true);
    } else {
      setIsChecked(false);
    }
  }, [props.membershipFee.product.id, props.toPayList]);

  const handleChange = (): void => {
    props.onChange(props.membershipFee);
  };

  const getPayCheckbox = (membershipFee: MemberProductStatus) => {
    if (membershipFee.isCompleted) {
      return (
        <VStack alignItems="flex-start" spacing={2}>
          <HStack>
            <Icon as={FaCalendarAlt} color="green.500" />
            <Text fontSize="sm" color={secondaryTextColor}>
              Fecha de pago: {date}
            </Text>
          </HStack>
          <HStack>
            <Icon as={FaCreditCard} color="green.500" />
            <Text fontSize="sm" color={secondaryTextColor}>
              Valor abonado: <CurrencyData value={props.membershipFee.paidAmount}/>
            </Text>
          </HStack>
        </VStack>
      )
    }
    else {
      const isBlockedByPreviousMonths = isBlockedByPreviousUnpaidMonths(
        membershipFee,
        props.allMembershipFees,
        props.toPayList
      );
      const checkbox = (
        <Checkbox
          width="100%"
          isChecked={isChecked}
          onChange={handleChange}
          disabled={paymentCheckboxIsDisabled(membershipFee, props.allMembershipFees, props.toPayList, isChecked)}
          colorScheme="orange"
          size="lg"
        >
          <Text fontWeight="semibold" color={checkboxTextColor}>
            Seleccionar para abonar
          </Text>
        </Checkbox>
      );

      return (
        <Box
          bg={checkboxBg}
          p={3}
          borderRadius="lg"
          border={`2px solid ${checkboxBorderColor}`}
          w="full"
        >
          {isBlockedByPreviousMonths && !isChecked ? (
            <Tooltip label={PREVIOUS_MONTHS_PAYMENT_REQUIRED_MESSAGE} placement="top">
              <Box>{checkbox}</Box>
            </Tooltip>
          ) : (
            checkbox
          )}
        </Box>
      )
    }
  }

  return (
    <Card
      width="100%"
      bg={cardBg}
      border={`2px solid ${perforatedBorder}`}
      boxShadow="xl"
      borderRadius="lg"
      overflow="hidden"
      position="relative"
      _hover={{
        transform: 'translateY(-4px)',
        boxShadow: '2xl',
        borderColor: hoverBorderColor
      }}
      transition="all 0.3s ease"
    >
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        height="8px"
        bg={perforationGradient}
        borderRadius="full"
      />

      <CardHeader bg={headerBg} borderBottom={`1px dashed ${headerBorderColor}`} py={3}>
        <HStack justify="space-between" align="center">
          <HStack>
            <Icon as={FaReceipt} color="blue.500" />
            <Heading size="md" color={textColor}>
              CUOTA SOCIAL
            </Heading>
          </HStack>
          <Badge
            colorScheme={badgeColor(props.membershipFee)}
            variant="solid"
            fontSize="xs"
            px={2}
            py={1}
            borderRadius="full"
          >
            {getBadgeLabel(props.membershipFee)}
          </Badge>
        </HStack>
      </CardHeader>

      <CardBody p={4}>
        <VStack spacing={3} align="stretch">
          <Box>
            <Text fontSize="lg" fontWeight="bold" color={textColor}>
              {props.membershipFee.productName}
            </Text>
            <Divider my={2} border={`1px dashed ${perforatedBorder}`} />
          </Box>

          <Text
            fontSize="sm"
            color={secondaryTextColor}
            minHeight="40px"
            lineHeight="1.4"
          >
            {
              isMobile
                ? props.membershipFee.product.shortName
                : props.membershipFee.product.description
            }
          </Text>

          <Box bg={priceBg} p={3} borderRadius="md" border={`1px solid ${priceBorderColor}`}>
            <HStack justify="space-between">
              <Text fontSize="sm" color={secondaryTextColor}>
                Importe:
              </Text>
              <Text fontSize="2xl" fontWeight="bold" color={priceColor}>
                <CurrencyData value={props.membershipFee.originalPrice}/>
              </Text>
            </HStack>
          </Box>
        </VStack>
      </CardBody>

      <CardFooter bg={footerBg} borderTop={`1px dashed ${perforatedBorder}`} p={4}>
        {getPayCheckbox(props.membershipFee)}
      </CardFooter>

      <Box
        position="absolute"
        bottom="0"
        left="0"
        right="0"
        height="8px"
        bg={perforationGradient}
        borderRadius="full"
      />
    </Card>
  );
}

export default MembershipFeeCard;
